import React from "react";
import { Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

export const Spinner = ({ className = "" }) => (
  <Loader2 className={`animate-spin ${className}`} />
);

export const PageLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="w-10 h-10 border-2 border-[#FF5C00] border-t-transparent rounded-full animate-spin" />
  </div>
);

export const EmptyState = ({ icon: Icon, title, text, action }) => (
  <div className="flex flex-col items-center justify-center text-center py-16 px-6">
    {Icon && (
      <div className="w-14 h-14 rounded-2xl bg-[#181818] border border-[#2A2A2A] flex items-center justify-center mb-4">
        <Icon size={24} className="text-neutral-500" />
      </div>
    )}
    <h3 className="text-lg font-semibold text-white">{title}</h3>
    {text && <p className="text-sm text-neutral-500 mt-1 max-w-sm">{text}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

export const StatCard = ({ label, value, sub, to }) => {
  const inner = (
    <>
      <div className="text-[13px] text-neutral-400">{label}</div>
      <div className="text-[30px] font-bold text-white mt-1">{value}</div>
      {sub && <div className="text-[12px] text-neutral-500 mt-1">{sub}</div>}
    </>
  );
  const base = "rounded-2xl border border-[#2A2A2A] bg-[#181818] p-5";
  if (to) return <Link to={to} data-testid={`statcard-${String(label).toLowerCase().replace(/\s+/g, "-")}`} className={`${base} block cursor-pointer transition-colors hover:border-[#FF5C00]`}>{inner}</Link>;
  return <div className={base}>{inner}</div>;
};

export const Badge = ({ children, tone = "neutral", ...rest }) => {
  const tones = {
    neutral: "bg-[#232326] text-neutral-300",
    orange: "bg-[#FF5C00]/15 text-[#FF7A2F]",
    green: "bg-[#1A8A4A]/15 text-[#2fbe6e]",
    red: "bg-[#CC2200]/15 text-[#ff6a4d]",
    amber: "bg-amber-500/15 text-amber-400",
  };
  return <span {...rest} className={`inline-flex items-center px-2.5 py-1 rounded-full text-[12px] font-medium ${tones[tone]}`}>{children}</span>;
};

export const fmtSEK = (n) => {
  if (n === null || n === undefined) return "-";
  return new Intl.NumberFormat("sv-SE").format(n).replace(/\s/g, "\u00a0") + "\u00a0SEK";
};

export const fmtDate = (s) => {
  if (!s) return "";
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

/* Eventdatumet, med hänsyn till att det kan vara ungefärligt.
 *
 * En kund som ännu inte spikat dagen väljer en månad, och den sparas som
 * månadens första dag så att sortering och "har eventet passerat" fungerar som
 * förut. **Talangen måste se att datumet är ungefärligt.** Skriver vi
 * "1 september" för någon som menar "någon gång i september" offereras fel dag,
 * och felet upptäcks först när avtalet ska signeras.
 */
export const fmtEventDate = (s, approximate) => {
  if (!s) return "";
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  if (!approximate) return fmtDate(s);
  const month = d.toLocaleDateString("sv-SE", { month: "long", year: "numeric" });
  return `${month.charAt(0).toUpperCase()}${month.slice(1)} (ungefär)`;
};

export const statusTone = (status) => ({
  sent: "orange", countered: "amber", accepted: "green", declined: "red",
  expired: "neutral", withdrawn: "neutral", confirmed: "green", new: "neutral", matching: "orange",
  responded: "orange", pending: "amber", approved: "green", rejected: "red", blocked: "red",
  pending_verification: "amber", pending_approval: "orange", published: "green", unpublished: "neutral",
}[status] || "neutral");
